import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/apiService';
import authService from '../../services/authService';
import { CurrencyData, InAppNotification, IssuerData } from '../../types';
import { queryKeys } from '../../config/queryClient';
import { DashboardColumnCount, DashboardLayout, DashboardWidgetId, DashboardWidthSpan } from '../../config/dashboardWidgets/types';
import type { DashboardWidgetMoveDirection, DashboardWidgetMoveOptions } from '../../config/dashboardWidgets';
import { getVisibleDashboardWidgetIds } from '../../config/dashboardWidgets/layoutRows';
import { useDashboardLayout } from '../../hooks/useDashboardLayout';
import { useDashboardLayoutEditor } from '../../hooks/useDashboardLayoutEditor';

export interface HomeScreenState {
  issuerDataProducts: IssuerData[];
  issuerDataNotional: IssuerData[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  includeWarrants: boolean;
  userData: any;
  exposureItems: Array<{ name: string; value: number; color: string }>;
  exposureLoading: boolean;
  notionalDisplayCurrency: string;
  layout: DashboardLayout;
  layoutReady: boolean;
  isEditingLayout: boolean;
  isSavingLayout: boolean;
  isLayoutDirty: boolean;
  canResetLayout: boolean;
  layoutSaveError: string | null;
}

export interface HomeScreenActions {
  setIncludeWarrants: (value: boolean) => void;
  onRefresh: () => Promise<void>;
  handleCurrencyPress: (currencyCode: string) => void;
  handleExpiryPress: (quarter: string) => void;
  handleExposurePress: (underlyingName: string) => void;
  handleIssuerPress: (issuerName: string) => void;
  handleCalendarDayPress: (date: string) => void;
  startLayoutEditing: () => void;
  cancelLayoutEditing: () => void;
  resetLayoutToDefault: () => void;
  saveLayout: () => Promise<void>;
  addDraftWidget: (widgetId: DashboardWidgetId) => void;
  removeDraftWidget: (widgetId: DashboardWidgetId) => void;
  moveDraftWidget: (
    widgetId: DashboardWidgetId,
    direction: DashboardWidgetMoveDirection,
    options?: DashboardWidgetMoveOptions
  ) => void;
  swapDraftWidgets: (sourceId: DashboardWidgetId, targetId: DashboardWidgetId) => void;
  setDraftWidgetWidthSpan: (widgetId: DashboardWidgetId, widthSpan: DashboardWidthSpan) => void;
  setDraftColumnCount: (columnCount: DashboardColumnCount) => void;
}

export interface HomeScreenProps {
  onLogout: () => void;
  onNavigateToProducts: () => void;
  onNavigateToHome?: () => void;
  onNavigateToEvents?: () => void;
  onNavigateToFilteredProducts?: (filters: {
    currency?: string;
    issuer?: string;
    maturityQuarter?: string;
    underlying?: string;
    eventType?: string;
    weekStart?: string;
    eventDate?: string;
    nearBarrier?: boolean;
    riskType?: 'barrier' | 'barrierHit' | 'lowStrike';
    maxDistancePct?: number;
    selectedProductTypes?: string[];
    selectedIssuers?: string[];
    ownershipScope?: 'group' | 'self';
    includeWarrants?: boolean;
  }) => void;
  onProductPress?: (isin: string) => void;
  notifications?: readonly InAppNotification[];
  onNotificationPress?: (notification: InAppNotification) => void;
  onGoToNotifications?: () => void;
  onNotificationsRefresh?: () => Promise<void> | void;
  ownershipScope?: 'group' | 'self';
  onOwnershipScopeChange?: (value: 'group' | 'self') => void;
  showOwnershipScopeToggle?: boolean;
  sidebarOpen?: boolean;
  sidebarCollapsed?: boolean;
}

export function useHomeScreen(props: HomeScreenProps) {
  const {
    onNavigateToFilteredProducts,
    ownershipScope = 'group',
    onNotificationsRefresh,
  } = props;
  const queryClient = useQueryClient();
  const { layout: savedLayout, layoutReady } = useDashboardLayout();
  const layoutEditor = useDashboardLayoutEditor(savedLayout);
  const layout = layoutEditor.displayLayout;
  const visibleWidgetIds = getVisibleDashboardWidgetIds(layout);

  const [issuerDataProducts, setIssuerDataProducts] = useState<IssuerData[]>([]);
  const [issuerDataNotional, setIssuerDataNotional] = useState<IssuerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeWarrants, setIncludeWarrants] = useState(true);
  const [userData, setUserData] = useState<any>(null);
  const [exposureItems, setExposureItems] = useState<
    Array<{ name: string; value: number; color: string }>
  >([]);
  const [exposureLoading, setExposureLoading] = useState(true);
  const [notionalDisplayCurrency, setNotionalDisplayCurrency] = useState('CHF');

  const getAutoDisplayCurrency = (currencies: CurrencyData[]): string => {
    const validCurrencies = currencies
      .filter(currency => Number.isFinite(currency.allocation))
      .sort((a, b) => b.allocation - a.allocation);

    if (validCurrencies.length === 0) {
      return 'USD';
    }

    const top = validCurrencies[0];
    const second = validCurrencies[1];
    const hasExactFiftyFiftyTie = Boolean(
      second &&
        Math.abs(top.allocation - 50) < 0.000001 &&
        Math.abs(second.allocation - 50) < 0.000001
    );

    if (hasExactFiftyFiftyTie) {
      return 'USD';
    }

    if (top.allocation > 50) {
      return top.currencyCode || 'USD';
    }

    return 'USD';
  };

  const fetchExposureData = async () => {
    if (!visibleWidgetIds.has('top_underlying_exposure')) {
      setExposureItems([]);
      setExposureLoading(false);
      return;
    }

    try {
      setExposureLoading(true);
      const data = await apiService.getTopUnderlyingExposure(
        'worst',
        includeWarrants,
        ownershipScope
      );
      setExposureItems(data);
    } catch (err) {
      console.error('Error fetching exposure data:', err);
      setExposureItems([]);
    } finally {
      setExposureLoading(false);
    }
  };

  const handleCurrencyPress = (currencyCode: string) => {
    if (layoutEditor.isEditing) {
      return;
    }
    console.log('🔍 Currency clicked:', currencyCode);
    onNavigateToFilteredProducts?.({ currency: currencyCode, ownershipScope, includeWarrants });
  };

  const handleExpiryPress = (quarter: string) => {
    if (layoutEditor.isEditing) {
      return;
    }
    console.log('🔍 Expiry quarter clicked:', quarter);
    onNavigateToFilteredProducts?.({
      maturityQuarter: 'Expiry ' + quarter,
      ownershipScope,
      includeWarrants,
    });
  };

  const handleExposurePress = (underlyingName: string) => {
    if (layoutEditor.isEditing) {
      return;
    }
    console.log('🔍 Underlying clicked:', underlyingName);
    onNavigateToFilteredProducts?.({ underlying: underlyingName, ownershipScope, includeWarrants });
  };

  const handleIssuerPress = (issuerName: string) => {
    if (layoutEditor.isEditing) {
      return;
    }
    console.log('🔍 Issuer clicked:', issuerName);
    onNavigateToFilteredProducts?.({ issuer: issuerName, ownershipScope, includeWarrants });
  };

  const handleCalendarDayPress = (date: string) => {
    if (layoutEditor.isEditing) {
      return;
    }
    onNavigateToFilteredProducts?.({
      eventType: 'Calendar',
      eventDate: date,
      ownershipScope,
      includeWarrants,
    });
  };

  const fetchIssuerRanking = async () => {
    const needsProducts = visibleWidgetIds.has('issuer_ranking_products');
    const needsNotional = visibleWidgetIds.has('issuer_ranking_notional');

    if (!needsProducts && !needsNotional) {
      setIssuerDataProducts([]);
      setIssuerDataNotional([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const isServerAvailable = await apiService.checkServerHealth();
      if (!isServerAvailable) {
        throw new Error('Server unavailable');
      }

      const productsPromise = needsProducts
        ? apiService.getStatisticsByIssuer(includeWarrants, ownershipScope)
        : Promise.resolve([] as IssuerData[]);
      const currencyPromise = needsNotional
        ? apiService.getNotionalByCurrency(includeWarrants, ownershipScope)
        : Promise.resolve([] as CurrencyData[]);

      const [dataProducts, currencyData] = await Promise.all([productsPromise, currencyPromise]);

      if (needsNotional) {
        const autoDisplayCurrency = getAutoDisplayCurrency(currencyData);
        const dataNotional = await apiService.getStatisticsByIssuerNotional(
          includeWarrants,
          ownershipScope,
          autoDisplayCurrency
        );
        setIssuerDataNotional(dataNotional);
        setNotionalDisplayCurrency(
          dataNotional[0]?.displayCurrency || autoDisplayCurrency || 'USD'
        );
      } else {
        setIssuerDataNotional([]);
      }

      if (needsProducts) {
        setIssuerDataProducts(dataProducts);
      } else {
        setIssuerDataProducts([]);
      }
    } catch (err) {
      console.error('Error fetching issuer ranking:', err);
      setError('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    if (layoutEditor.isEditing) {
      return;
    }

    setRefreshing(true);
    try {
      const refreshTasks: Array<Promise<unknown>> = [queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.layout() })];

      if (
        visibleWidgetIds.has('issuer_ranking_products') ||
        visibleWidgetIds.has('issuer_ranking_notional')
      ) {
        refreshTasks.push(fetchIssuerRanking());
      }

      if (visibleWidgetIds.has('top_underlying_exposure')) {
        refreshTasks.push(fetchExposureData());
      }

      if (visibleWidgetIds.has('favourites_watchlist')) {
        refreshTasks.push(
          queryClient.invalidateQueries({ queryKey: queryKeys.favourites.watchlists() })
        );
      }

      if (visibleWidgetIds.has('events_calendar')) {
        refreshTasks.push(
          queryClient.invalidateQueries({ queryKey: queryKeys.events.all })
        );
      }

      if (visibleWidgetIds.has('notifications') && onNotificationsRefresh) {
        refreshTasks.push(Promise.resolve(onNotificationsRefresh()));
      }

      await Promise.all(refreshTasks);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      const profile = await authService.getUserProfile();
      if (profile) {
        setUserData(profile);
      } else {
        const currentUser = authService.getCurrentUser();
        setUserData(currentUser);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (!layoutReady) {
      return;
    }

    fetchIssuerRanking();
    fetchExposureData();
  }, [includeWarrants, ownershipScope, layoutReady, layout]);

  const state: HomeScreenState = {
    issuerDataProducts,
    issuerDataNotional,
    loading,
    refreshing,
    error,
    includeWarrants,
    userData,
    exposureItems,
    exposureLoading,
    notionalDisplayCurrency,
    layout,
    layoutReady,
    isEditingLayout: layoutEditor.isEditing,
    isSavingLayout: layoutEditor.isSaving,
    isLayoutDirty: layoutEditor.isDirty,
    canResetLayout: layoutEditor.canReset,
    layoutSaveError: layoutEditor.saveError,
  };

  const actions: HomeScreenActions = {
    setIncludeWarrants,
    onRefresh,
    handleCurrencyPress,
    handleExpiryPress,
    handleExposurePress,
    handleIssuerPress,
    handleCalendarDayPress,
    startLayoutEditing: layoutEditor.startEditing,
    cancelLayoutEditing: layoutEditor.cancelEditing,
    resetLayoutToDefault: layoutEditor.resetToDefault,
    saveLayout: layoutEditor.save,
    addDraftWidget: layoutEditor.addWidget,
    removeDraftWidget: layoutEditor.removeWidget,
    moveDraftWidget: layoutEditor.moveWidget,
    swapDraftWidgets: layoutEditor.swapWidgets,
    setDraftWidgetWidthSpan: layoutEditor.setWidgetWidthSpan,
    setDraftColumnCount: layoutEditor.setColumnCount,
  };

  return { state, actions };
}
